<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { Link, links } from "svelte-routing";
  import { fade, slide } from "svelte/transition";

  const dispatch = createEventDispatcher();
  let open = false;
  export let theme;
  let prevScrollPos = 0;
  let isNavbarVisible = true;
  let isHoveringTop = false;

  const toggleTheme = () => dispatch("toggleTheme");

  onMount(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  function handleScroll() {
    const currentScrollPos = window.pageYOffset;
    isNavbarVisible =
      prevScrollPos > currentScrollPos ||
      currentScrollPos < 10 ||
      isHoveringTop;
    prevScrollPos = currentScrollPos;
  }

  function handleNavClick(e, href) {
    // Skip handling for full routes (those that don't start with /#)
    if (!href.startsWith("/#")) return;

    e.preventDefault();
    const targetId = href.replace("/#", "");
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      open = false; // Close mobile menu if open
      history.pushState({}, "", href);
    }
  }

  const navItems = [
    { href: "/#experience", text: "Experience", icon: "briefcase" },
    { href: "/#app-show-case", text: "App's", icon: "mobile" },
    { href: "/#web3-projects", text: "Web3 Projects", icon: "globe" },
    { href: "/#blog", text: "Blog", icon: "book" },
    { href: "/#recommendations", text: "Recommendations", icon: "star" },
    { href: "/#contact", text: "Contact", icon: "envelope" },
  ];
</script>

<nav
  id="navbar"
  class="fixed top-0 w-full z-50 transition-all duration-300"
  class:translate-y-0={isNavbarVisible}
  class:-translate-y-full={!isNavbarVisible}
  on:mouseenter={() => (isHoveringTop = true)}
  on:mouseleave={() => (isHoveringTop = false)}
>
  <!-- Backdrop -->
  <div
    class="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-lg"
  ></div>

  <!-- Content -->
  <div class="relative flex items-center gap-4 px-6 lg:px-10 py-4">
    <!-- Logo -->
    <a
      href="/#home"
      class="group relative overflow-hidden rounded-xl bg-gradient-to-br
      from-blue-500 to-purple-500 w-12 h-12
      flex items-center justify-center
      transform transition-all duration-500 hover:scale-105 hover:-rotate-3
      hover:shadow-lg hover:shadow-blue-500/20"
    >
      <span
        class="text-4xl font-black text-white transform transition-all duration-500
        group-hover:scale-110 group-hover:rotate-12"
      >
        M
      </span>

      <!-- Particle effects -->
      <div class="absolute inset-0">
        {#each Array(3) as _, i}
          <span
            class="absolute w-1 h-1 rounded-full bg-white/60
            animate-particle-{i + 1} opacity-0 group-hover:opacity-100
            transition-opacity duration-300"
            style="left: {30 * i}%; top: {20 * i}%"
          >
          </span>
        {/each}
      </div>
    </a>

    <a
      href="/#home"
      class="text-lg font-medium text-gray-900 dark:text-white
      hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
    >
      Manu KJ
    </a>

    <!-- Desktop Navigation -->
    <div use:links class="hidden lg:flex items-center gap-6 ml-auto">
      {#each navItems as { href, text, icon, isFullRoute }}
        {#if isFullRoute}
          <Link
            to={href}
            class="group relative px-2 py-2 text-gray-600 dark:text-gray-300
            hover:text-blue-500 dark:hover:text-blue-400 transition-all duration-300"
          >
            <span
              class="inline-block mr-1 opacity-0 -translate-x-3 group-hover:opacity-100
              group-hover:translate-x-0 transition-all duration-300"
            >
              <i class="fas fa-{icon} text-sm"></i>
            </span>

            <span class="relative">
              {text}
              <span
                class="absolute -bottom-1 left-0 w-0 h-0.5
                bg-gradient-to-r from-blue-500 to-purple-500
                group-hover:w-full transition-all duration-300"
              ></span>
            </span>

            <span
              class="absolute inset-0 rounded-lg bg-blue-50 dark:bg-blue-500/10
              opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100
              transition-all duration-300 -z-10"
            ></span>
          </Link>
        {:else}
          <a
            {href}
            on:click={(e) => handleNavClick(e, href)}
            class="group relative px-2 py-2 text-gray-600 dark:text-gray-300
            hover:text-blue-500 dark:hover:text-blue-400 transition-all duration-300"
          >
            <span
              class="inline-block mr-1 opacity-0 -translate-x-3 group-hover:opacity-100
              group-hover:translate-x-0 transition-all duration-300"
            >
              <i class="fas fa-{icon} text-sm"></i>
            </span>

            <span class="relative">
              {text}
              <span
                class="absolute -bottom-1 left-0 w-0 h-0.5
                bg-gradient-to-r from-blue-500 to-purple-500
                group-hover:w-full transition-all duration-300"
              ></span>
            </span>

            <span
              class="absolute inset-0 rounded-lg bg-blue-50 dark:bg-blue-500/10
              opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100
              transition-all duration-300 -z-10"
            ></span>
          </a>
        {/if}
      {/each}

      <!-- Add a small divider before theme toggle -->
      <div class="w-px h-6 bg-gray-200 dark:bg-gray-700"></div>

      <!-- Theme Toggle -->
      <button
        on:click={toggleTheme}
        class="group relative px-4 py-2 rounded-xl overflow-hidden
        bg-gradient-to-r from-gray-100 to-gray-50
        dark:from-gray-800 dark:to-gray-700
        text-gray-700 dark:text-gray-300
        hover:shadow-md hover:-translate-y-0.5
        transition-all duration-300"
      >
        <span
          class="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10
          translate-y-full group-hover:translate-y-0 transition-transform duration-500"
        ></span>

        <div class="relative flex items-center gap-2">
          <i
            class="fas fa-{theme === 'dark'
              ? 'moon'
              : 'sun'} transform transition-all duration-700
            group-hover:rotate-[360deg] group-hover:scale-110"
          ></i>
          <span class="font-medium">{theme === "dark" ? "Dark" : "Light"}</span>
        </div>
      </button>
    </div>

    <!-- Mobile Menu Button -->
    <button
      on:click={() => (open = !open)}
      class="lg:hidden ml-auto p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800
      text-gray-600 dark:text-gray-300 transition-colors"
      aria-label="Toggle menu"
    >
      <i class="fas fa-{open ? 'times' : 'bars'} w-6 h-6"></i>
    </button>
  </div>
</nav>

<!-- Mobile Menu -->
{#if open}
  <div
    on:click={() => (open = false)}
    on:keydown={(e) => e.key === "Escape" && (open = false)}
    transition:fade={{ duration: 200 }}
    class="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40"
    role="button"
    tabindex="0"
  ></div>

  <div
    transition:slide={{ duration: 300 }}
    class="fixed top-[73px] left-0 right-0 z-50 bg-white dark:bg-gray-900
    border-t border-gray-200 dark:border-gray-800 shadow-xl"
  >
    <nav class="flex flex-col divide-y divide-gray-200 dark:divide-gray-800">
      {#each navItems as { href, text, icon, isFullRoute }}
        {#if isFullRoute}
          <Link
            to={href}
            class="flex items-center gap-3 px-6 py-4 text-gray-600 dark:text-gray-300
            hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i class="fas fa-{icon} text-blue-500 dark:text-blue-400"></i>
            {text}
          </Link>
        {:else}
          <a
            {href}
            on:click={(e) => handleNavClick(e, href)}
            class="flex items-center gap-3 px-6 py-4 text-gray-600 dark:text-gray-300
            hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <i class="fas fa-{icon} text-blue-500 dark:text-blue-400"></i>
            {text}
          </a>
        {/if}
      {/each}

      <div class="p-4">
        <button
          on:click={toggleTheme}
          class="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl
          bg-gray-100 dark:bg-gray-800
          hover:bg-gray-200 dark:hover:bg-gray-700
          text-gray-700 dark:text-gray-300
          transition-all duration-300"
        >
          <i class="fas fa-{theme === 'dark' ? 'moon' : 'sun'}"></i>
          <span class="font-medium"
            >{theme === "dark" ? "Dark Mode" : "Light Mode"}</span
          >
        </button>
      </div>
    </nav>
  </div>
{/if}

<style>
  @keyframes particle-1 {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-10px, -10px);
    }
  }

  @keyframes particle-2 {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(10px, -15px);
    }
  }

  @keyframes particle-3 {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-5px, -20px);
    }
  }

  .animate-particle-1 {
    animation: particle-1 2s ease-in-out infinite;
  }
  .animate-particle-2 {
    animation: particle-2 2.5s ease-in-out infinite;
  }
  .animate-particle-3 {
    animation: particle-3 3s ease-in-out infinite;
  }
</style>
